import { useCallback, useEffect, useRef, useState } from 'react';
import { MicroAppClient } from '@byte-coach/hutbot-mapp-sdk-js';
import {
  redeemMappToken,
  verifyOTP as verifyOTPApi,
  requestOTP as requestOTPApi,
  completeOnboarding as completeOnboardingApi,
  getUserPreferences as getUserPreferencesApi,
  completeUnifiedOnboarding as completeUnifiedOnboardingApi,
  type User,
  type OnboardingPreferences,
} from '../api';
import NetworkManager from '../api/NetworkManager';
import { environmentConfig, currentEnvironment } from '../config';
import type { Environment } from '@mapp-byte-connect/config';

const STORAGE_KEYS = {
  mappToken: 'mapp_token',
  accessToken: 'access_token',
  userData: 'user_data',
  environment: 'mapp_environment',
  brandId: 'mapp_brand_id',
  authMethod: 'auth_method',
} as const;

type AuthMethod = 'mapp' | 'email' | null;
type AuthStep =
  | 'idle'
  | 'email-input'
  | 'otp-verification'
  | 'authenticated'
  | 'onboarding'
  | 'mapp-denied';

/** True when running inside HutBot mobile (window.mapp injected) - MApp-only auto-login */
const isMAppContext = (): boolean =>
  typeof window !== 'undefined' && !!(window as { mapp?: unknown }).mapp;

interface UseAuthReturn {
  // Auth state
  isLoading: boolean;
  user: User | undefined;
  authMethod: AuthMethod;
  authStep: AuthStep;
  isFirstLogin: boolean;
  isMAppContext: boolean;

  // Environment
  brandId: string | null;
  environment: Environment;
  setBrandContext: (brandId: string | null) => void;

  // MApp auth
  initMAppAuth: () => void;

  // Email/OTP auth
  requestOTP: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyOTP: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  pendingEmail: string | null;

  // Onboarding
  completeOnboarding: (preferences: OnboardingPreferences) => Promise<{ success: boolean; error?: string }>;
  skipOnboarding: () => void;

  // Internal onboarding tour
  showInternalOnboarding: boolean;
  completeInternalOnboarding: (preferences?: { timezone: string; emailNotifications: boolean }) => void;
  triggerOnboarding: () => void;
  dismissOnboarding: () => void;

  // Common
  logout: () => void;
  setAuthStep: (step: AuthStep) => void;
}

interface MAppAuthQueryParams {
  mappToken?: string;
  environment?: Environment;
  brand?: string;
}

const getMAppClient = () => {
  return new MicroAppClient({
    mappId: environmentConfig.APP_MAPP_ID,
    authURL: environmentConfig.APP_MAPP_AUTH_URL,
  });
};

function normalizeEnvironment(env: string): Environment {
  const envLower = env.toLowerCase();
  if (envLower === 'local' || envLower === 'simulator' || envLower === 'dev' || envLower === 'staging' || envLower === 'prod') {
    return envLower as Environment;
  }
  console.warn(`Unknown environment '${env}' received, defaulting to 'local'`);
  return 'local';
}

export function useAuth(): UseAuthReturn {
  const urlSearchParamsRef = useRef(new URLSearchParams(window.location.search));
  const params = Object.fromEntries(urlSearchParamsRef.current.entries()) as MAppAuthQueryParams;

  NetworkManager.setEnvironment(currentEnvironment);
  if (params.brand) {
    NetworkManager.setBrandId(params.brand);
  }

  const clientRef = useRef(getMAppClient());
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(
    (localStorage.getItem(STORAGE_KEYS.authMethod) as AuthMethod) || null
  );
  const [authStep, setAuthStep] = useState<AuthStep>('idle');
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(
    params.brand || localStorage.getItem(STORAGE_KEYS.brandId) || null
  );
  const [environment, setEnvironment] = useState<Environment>(
    normalizeEnvironment(params.environment || localStorage.getItem(STORAGE_KEYS.environment) || currentEnvironment)
  );
  const [showInternalOnboarding, setShowInternalOnboarding] = useState(false);

  const getInternalOnboardingKey = useCallback((yumId: string) => {
    return `onboarding_completed_internal_${yumId}`;
  }, []);

  const setBrandContext = useCallback((brand: string | null) => {
    NetworkManager.setBrandId(brand);
    setBrandId(brand);
    if (brand) {
      localStorage.setItem(STORAGE_KEYS.brandId, brand);
    } else {
      localStorage.removeItem(STORAGE_KEYS.brandId);
    }
  }, []);

  // Clear all auth storage
  const cleanAuthToken = useCallback(() => {
    NetworkManager.clearAuthorizationToken();
    NetworkManager.setBrandId(null);
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    setBrandId(null);
    setAuthMethod(null);
    setUser(undefined);
    setAuthStep('idle');
    setIsFirstLogin(false);
    setPendingEmail(null);
    setShowInternalOnboarding(false);
  }, []);

  // Logout handler
  const logout = useCallback(() => {
    cleanAuthToken();
    // For MApp auth, redirect to auth URL
    // For email auth, just clear state and show login page
    if (authMethod === 'mapp') {
      window.location.href = environmentConfig.APP_MAPP_AUTH_URL;
    }
  }, [cleanAuthToken, authMethod]);

  // Request OTP for email authentication
  const requestOTP = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      const response = await requestOTPApi(email);
      if (response.status) {
        setPendingEmail(email);
        setAuthStep('otp-verification');
        return { success: true };
      }
      return { success: false, error: 'Failed to send verification code' };
    } catch (error: unknown) {
      // Extract user-friendly message from API response
      let message = 'Unable to send verification code. Please try again.';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string }; status?: number } };
        if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        } else if (axiosError.response?.status === 403) {
          message = 'Your email is not authorized. Please contact your administrator for access.';
        }
      }
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Verify OTP and complete email authentication
  const verifyOTP = useCallback(async (
    email: string,
    code: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      const response = await verifyOTPApi(email, code);
      if (response.status && response.accessToken) {
        // Set auth state
        NetworkManager.setAuthorizationToken(response.accessToken);
        setUser(response.data);
        setAuthMethod('email');
        setIsFirstLogin(response.isFirstLogin);

        // Store in localStorage
        localStorage.setItem(STORAGE_KEYS.accessToken, response.accessToken);
        localStorage.setItem(STORAGE_KEYS.userData, JSON.stringify(response.data));
        localStorage.setItem(STORAGE_KEYS.authMethod, 'email');
        localStorage.setItem(STORAGE_KEYS.environment, environment);

        // Navigate to onboarding if first login, otherwise authenticated
        if (response.isFirstLogin) {
          setAuthStep('onboarding');
        } else {
          setAuthStep('authenticated');
        }

        return { success: true };
      }
      return { success: false, error: 'Invalid verification code' };
    } catch (error: unknown) {
      // Extract user-friendly message from API response
      let message = 'Unable to verify code. Please try again.';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        }
      }
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [environment]);

  // Complete onboarding
  const completeOnboarding = useCallback(async (
    preferences: OnboardingPreferences
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      // Call both the legacy endpoint (for backward compat) and the unified endpoint
      const [legacyResponse] = await Promise.all([
        completeOnboardingApi(preferences),
        completeUnifiedOnboardingApi(preferences).catch(() => {
          // Non-fatal: legacy endpoint is the primary source of truth for vendors
        }),
      ]);
      if (legacyResponse.status) {
        // Update user with onboarding completed
        if (user) {
          const updatedUser = { ...user, onboardingCompleted: true };
          setUser(updatedUser);
          localStorage.setItem(STORAGE_KEYS.userData, JSON.stringify(updatedUser));
        }
        setIsFirstLogin(false);
        setAuthStep('authenticated');
        return { success: true };
      }
      return { success: false, error: 'Unable to complete setup. Please try again.' };
    } catch (error: unknown) {
      // Extract user-friendly message from API response
      let message = 'Unable to complete setup. Please try again.';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        }
      }
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Skip onboarding
  const skipOnboarding = useCallback(() => {
    setIsFirstLogin(false);
    setAuthStep('authenticated');
  }, []);

  // Internal onboarding tour callbacks
  const completeInternalOnboarding = useCallback(
    (preferences?: { timezone: string; emailNotifications: boolean }) => {
      if (user?.yumId) {
        localStorage.setItem(getInternalOnboardingKey(user.yumId), 'true');
      }
      setShowInternalOnboarding(false);

      // Persist to backend (fire-and-forget, localStorage is the fast cache)
      completeUnifiedOnboardingApi(
        preferences
          ? { timezone: preferences.timezone, notificationPreferences: { email: preferences.emailNotifications } }
          : undefined
      ).catch(() => {
        // Non-fatal: localStorage already persisted above
      });
    },
    [user?.yumId, getInternalOnboardingKey]
  );

  const triggerOnboarding = useCallback(() => {
    setShowInternalOnboarding(true);
  }, []);

  const dismissOnboarding = useCallback(() => {
    if (user?.yumId) {
      localStorage.setItem(getInternalOnboardingKey(user.yumId), 'true');
    }
    setShowInternalOnboarding(false);

    // Persist dismissal to backend (fire-and-forget)
    completeUnifiedOnboardingApi().catch(() => {
      // Non-fatal: localStorage already persisted above
    });
  }, [user?.yumId, getInternalOnboardingKey]);

  // Initialize MApp authentication
  const initMAppAuth = useCallback(() => {
    clientRef.current.authenticate();
  }, []);

  // Update permissions for MApp authentication
  const updateMAppPermissions = useCallback(async () => {
    try {
      const mappToken =
        urlSearchParamsRef.current.get('mappToken') || localStorage.getItem(STORAGE_KEYS.mappToken);
      // For local development, always use 'local' regardless of localStorage
      // This prevents issues when switching between local and deployed environments
      const envParam =
        currentEnvironment === 'local'
          ? 'local'
          : urlSearchParamsRef.current.get('environment') ||
            localStorage.getItem(STORAGE_KEYS.environment) ||
            currentEnvironment;
      const brandIdParam =
        urlSearchParamsRef.current.get('brand') || localStorage.getItem(STORAGE_KEYS.brandId) || undefined;
      const normalizedEnv = normalizeEnvironment(envParam);

      if (!mappToken) {
        return false;
      }

      NetworkManager.setEnvironment(normalizedEnv);
      NetworkManager.setBrandId(brandIdParam ?? null);
      setEnvironment(normalizedEnv);
      setBrandId(brandIdParam ?? null);

      const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      const cachedUser = localStorage.getItem(STORAGE_KEYS.userData);

      if (accessToken && cachedUser) {
        NetworkManager.setAuthorizationToken(accessToken);
        setUser(JSON.parse(cachedUser));
        setAuthMethod('mapp');
        setAuthStep('authenticated');
        return true;
      }

      const permissionResp = await redeemMappToken(mappToken);
      if (permissionResp.status) {
        NetworkManager.setAuthorizationToken(permissionResp.accessToken);
        setUser(permissionResp.data);
        setAuthMethod('mapp');
        setAuthStep('authenticated');
        localStorage.setItem(STORAGE_KEYS.mappToken, mappToken);
        localStorage.setItem(STORAGE_KEYS.accessToken, permissionResp.accessToken);
        localStorage.setItem(STORAGE_KEYS.userData, JSON.stringify(permissionResp.data));
        localStorage.setItem(STORAGE_KEYS.environment, normalizedEnv);
        localStorage.setItem(STORAGE_KEYS.authMethod, 'mapp');
        if (brandIdParam) {
          localStorage.setItem(STORAGE_KEYS.brandId, brandIdParam);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error({ error }, 'Failed to get MApp permissions');
      return false;
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      setIsLoading(true);

      // Check for existing access token and user data
      const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      const cachedUser = localStorage.getItem(STORAGE_KEYS.userData);
      const savedAuthMethod = localStorage.getItem(STORAGE_KEYS.authMethod) as AuthMethod;

      if (accessToken && cachedUser) {
        try {
          NetworkManager.setAuthorizationToken(accessToken);
          // Restore brandId to NetworkManager from localStorage
          const savedBrandId = localStorage.getItem(STORAGE_KEYS.brandId);
          if (savedBrandId) {
            NetworkManager.setBrandId(savedBrandId);
          }
          setUser(JSON.parse(cachedUser));
          setAuthMethod(savedAuthMethod || 'mapp');
          setAuthStep('authenticated');
          setIsLoading(false);
          return;
        } catch {
          // Invalid cached data, continue to fresh auth
        }
      }

      // Try MApp token from URL params
      const hasMappToken = await updateMAppPermissions();
      if (hasMappToken) {
        setIsLoading(false);
        return;
      }

      // MApp context (HutBot mobile): auto-trigger MApp auth, no login page
      if (isMAppContext()) {
        const mapp = (window as { mapp?: { authenticate?: (payload?: { mappId?: string }) => void } }).mapp;
        if (mapp?.authenticate) {
          mapp.authenticate({ mappId: environmentConfig.APP_MAPP_ID });
          // Stay in loading; token will arrive via postMessage -> onReceivedRedeemableToken
          return;
        }
      }

      // No existing session, show login page
      setIsLoading(false);
    };

    checkExistingSession();
  }, [updateMAppPermissions]);

  // Subscribe to MApp SDK events
  useEffect((): (() => void) => {
    const onReceivedRedeemableToken = async (data: {
      mappToken: string;
      environment?: string;
      brand?: string;
    }) => {
      const { mappToken, environment: env, brand } = data;
      // For local development, always use 'local' regardless of what SDK returns
      const normalizedEnv =
        currentEnvironment === 'local' ? 'local' : normalizeEnvironment(env ?? currentEnvironment);
      try {
        localStorage.setItem(STORAGE_KEYS.mappToken, mappToken);
        localStorage.setItem(STORAGE_KEYS.environment, normalizedEnv);
        if (brand) {
          localStorage.setItem(STORAGE_KEYS.brandId, brand);
        }
        setBrandId(brand ?? null);
        setEnvironment(normalizedEnv);
        // Clear URL params
        const urlSearchParams = new URLSearchParams(window.location.search);
        urlSearchParams.delete('mappToken');
        urlSearchParams.delete('environment');
        urlSearchParams.delete('brand');
        window.history.pushState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('Failed to save tokens:', err);
      }
      await updateMAppPermissions();
    };

    const handleError = (error: unknown) => {
      if (error) {
        console.log('MAPP SDK error', error);
      }
    };

    const onRequestAuthorizationDenied = () => {
      if (isMAppContext()) {
        setAuthStep('mapp-denied');
        setIsLoading(false);
      }
    };

    const unsub = clientRef.current.subscribe({
      onReceivedRedeemableToken,
      onRequestAuthorizationDenied,
      onError: handleError,
    });

    return unsub as () => void;
  }, [updateMAppPermissions]);

  // Update loading state when user changes
  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }
  }, [user]);

  // Check if internal onboarding tour should be shown for MApp users
  useEffect(() => {
    if (authStep === 'authenticated' && authMethod === 'mapp' && user?.yumId) {
      const key = getInternalOnboardingKey(user.yumId);

      // Check localStorage cache first for instant UI
      if (localStorage.getItem(key)) {
        return; // Already completed locally
      }

      // Check backend for persisted onboarding state
      getUserPreferencesApi()
        .then((resp) => {
          if (resp.status && resp.data?.onboardingCompleted) {
            // Backend says completed – cache locally
            localStorage.setItem(key, 'true');
          } else {
            // Not completed – show the tour
            setShowInternalOnboarding(true);
          }
        })
        .catch(() => {
          // API unreachable – fall back to showing tour (localStorage already checked above)
          setShowInternalOnboarding(true);
        });
    }
  }, [authStep, authMethod, user?.yumId, getInternalOnboardingKey]);

  // Register 401 handler to clear auth state when token is invalid/expired
  useEffect(() => {
    NetworkManager.setOnUnauthorized(() => {
      console.log('Received 401 Unauthorized - clearing auth state');
      cleanAuthToken();
    });

    return () => {
      NetworkManager.setOnUnauthorized(null);
    };
  }, [cleanAuthToken]);

  return {
    isLoading,
    user,
    authMethod,
    authStep,
    isFirstLogin,
    isMAppContext: isMAppContext(),
    brandId,
    environment,
    setBrandContext,
    initMAppAuth,
    requestOTP,
    verifyOTP,
    pendingEmail,
    completeOnboarding,
    skipOnboarding,
    showInternalOnboarding,
    completeInternalOnboarding,
    triggerOnboarding,
    dismissOnboarding,
    logout,
    setAuthStep,
  };
}
